import { useRef, useState } from 'react';

import Slider from 'react-slick';

import { useAppDispatch } from 'dux/app/hooks';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';
import { mediaMinWidth } from '@prose-ui/utils/media';

import Collapse from 'Components/Collapse';

import arrowLeftIcon from 'assets/images/homeRevamp/ArrowLeft.svg';
import arrowRightIcon from 'assets/images/homeRevamp/ArrowRight.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { trackHeapEvent } from 'dux/tracking/actions';

import { CarouselDots } from './CarouselDots';

type CustomProps = {
  list: {
    title: string;
    description: string;
  }[];
};

export type AccordionContent = {
  custom: CustomProps;
  scientificResults: ScientificResultsProps;
  beforeAndAfter: BeforeAndAfterProps;
  fragranceFree: string;
};

type Props = {
  content: AccordionContent;
};

type ScientificResultsProps = {
  title: string;
  list: {
    alt: string;
    icon: string;
    text: string;
  }[];
  mentions: string[];
};

type BeforeAndAfterProps = {
  title: string;
  slider: {
    title: string;
    description: string;
    imgSrc: {
      src: string;
    };
    alt: string;
  }[];
  mention: string;
};

const NumberContainer = styled.div`
  width: 18px;
`;

const StyledSlider = styled(Slider)`
  width: 100%;

  .slick-track {
    display: flex;
    justify-content: flex-end;
  }

  .slick-slide {
    padding-left: ${theme.spacing['5x']};
  }

  ${mediaMinWidth('md')} {
    .slick-slide {
      padding: 0 ${theme.spacing['2x']};
    }
  }
`;

const StyledImage = styled.img`
  border-radius: 10px;
  object-fit: contain;
`;

const SliderControlContainer = styled.div`
  display: flex;
  align-self: center;
  justify-content: space-between;
  width: 245px;
`;

const ArrowButton = styled.button`
  cursor: pointer;

  display: none;

  width: 24px;
  height: 24px;
  border: none;

  background-color: transparent;

  ${mediaMinWidth('md')} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  color: ${theme.colors.primary[300]};
`;

const CustomBlock = ({ list }: CustomProps) => (
  <Box display="flex" flexDirection="column" gap="4x" paddingTop="2x">
    {list.map(({ title, description }, index) => (
      <Box key={title} display="flex" flexDirection="row" gap="4x">
        <NumberContainer>
          <Text color="primary.300" variant="body4medium">
            0{index + 1}
          </Text>
        </NumberContainer>
        <Box>
          <Box>
            <Text color="primary.300" variant="body4medium">
              {title}
            </Text>
          </Box>
          <Box>
            <Text color="primary.300" variant="body4regular">
              {description}
            </Text>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

const ScientificResults = ({ title, list, mentions }: ScientificResultsProps) => (
  <Box display="flex" flexDirection="column" gap="4x" paddingTop="2x">
    <Text color="primary.300" variant="body4regular">
      {title}
    </Text>
    <Box>
      {list.map(({ alt, icon, text }) => (
        <Box
          key="text"
          borderBottom="1px"
          borderColor="neutral.300"
          display="flex"
          gap="2x"
          paddingY="2x"
        >
          <img alt={alt} height={24} src={icon} width={24} />
          <Text color="primary.300" variant="body4regular">
            {text}
          </Text>
        </Box>
      ))}
    </Box>
    <Box display="flex" flexDirection="column" gap="4x">
      {mentions.map((mention) => (
        <Text key={mention} color="neutral.800" variant="body5regular">
          {mention}
        </Text>
      ))}
    </Box>
  </Box>
);

const BeforeAndAfter = ({ title, slider, mention }: BeforeAndAfterProps) => {
  const dispatch = useAppDispatch();
  const [index, setIndex] = useState(0);
  const sliderRef = useRef<Slider>(null);

  const slidesToShow = 1.1;

  const onClickArrow = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      if (direction === 'left') {
        sliderRef.current.slickPrev();
      } else {
        sliderRef.current.slickNext();
      }
    }
  };

  const onClickDot = (dotIndex: number) => {
    sliderRef.current?.slickGoTo(dotIndex, false);
    setIndex(dotIndex);
  };

  return (
    <Box display="flex" flexDirection="column" gap="4x" paddingTop="2x">
      <Box paddingX={{ mobile: '6x', tablet: 'none', desktop: 'none' }}>
        <Text color="primary.300" variant="body4regular">
          {title}
        </Text>
      </Box>
      <StyledSlider
        ref={sliderRef}
        afterChange={(currentSlide) => {
          dispatch(trackHeapEvent('Before After Module Carousel Scroll'));
          setIndex(currentSlide);
        }}
        arrows={false}
        dots={false}
        infinite
        slidesToShow={slidesToShow}
      >
        {slider.map(({ title, description, imgSrc, alt }) => (
          <Box key={description}>
            <StyledImage alt={alt} src={imgSrc.src} width="100%" />
            <Text color="primary.300" paddingTop="4x" variant="body4medium">
              {title}
            </Text>
            <Text color="primary.300" variant="body4regular">
              {description}
            </Text>
          </Box>
        ))}
      </StyledSlider>
      <Box
        display="flex"
        flexDirection="column"
        gap="4x"
        paddingX={{ mobile: '6x', tablet: 'none', desktop: 'none' }}
        width="full"
      >
        <Text color="primary.300" variant="body5regular">
          {mention}
        </Text>
        <SliderControlContainer>
          <Box display="flex" flex={1}>
            <ArrowButton
              data-click="change-slide-arrow"
              data-from="before-after-module-carousel"
              onClick={() => onClickArrow('left')}
              type="button"
            >
              <img alt="go-previous" height={14} src={arrowLeftIcon} width={7} />
            </ArrowButton>
          </Box>
          <CarouselDots
            currentIndex={Math.round(index)}
            dotsQuantity={slider.length}
            onClick={onClickDot}
          />
          <Box display="flex" flex={1}>
            <ArrowButton
              data-click="change-slide-arrow"
              data-from="before-after-module-carousel"
              onClick={() => onClickArrow('right')}
              type="button"
            >
              <img alt="go-next" height={14} src={arrowRightIcon} width={7} />
            </ArrowButton>
          </Box>
        </SliderControlContainer>
      </Box>
    </Box>
  );
};

const StyledCollapse = styled(Collapse)`
  width: 100%;
  padding-top: ${theme.spacing['5x']};
`;

const AnswerContainer = styled.div`
  padding: 0 ${theme.spacing['6x']};

  ${mediaMinWidth('md')} {
    padding: 0;
  }
`;

const Separator = styled.div`
  align-self: center;
  width: calc(100% - 2 * ${theme.spacing['6x']});
  height: 1px;
  background-color: ${theme.colors.neutral[400]};

  ${mediaMinWidth('md')} {
    width: 100%;
  }
`;

export const AccordionBlock = ({ content }: Props) => {
  const { isMobile } = useResponsiveVariant({ legacy: false });
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  return (
    <Container>
      <Separator />
      <StyledCollapse
        answer={
          <AnswerContainer>
            <CustomBlock list={content.custom.list} />
          </AnswerContainer>
        }
        chevronPadding={isMobile ? '0px 60px 0px 0px' : undefined}
        dataClick="toggle-collapse"
        dataFrom="accordion-1"
        isOpen={openedIndex === 0}
        onClick={() => setOpenedIndex(0)}
        question={
          <Text alignSelf="center" color="primary.300" variant="label2medium">
            HOW PROSE DOES CUSTOM
          </Text>
        }
        questionStyle={
          isMobile
            ? {
                padding: '0 24px',
              }
            : {}
        }
        scrollIntoViewOnOpen
      />
      <Separator />
      <StyledCollapse
        answer={
          <AnswerContainer>
            <ScientificResults
              list={content.scientificResults.list}
              mentions={content.scientificResults.mentions}
              title={content.scientificResults.title}
            />
          </AnswerContainer>
        }
        chevronPadding={isMobile ? '0px 60px 0px 0px' : undefined}
        dataClick="toggle-collapse"
        dataFrom="accordion-2"
        isOpen={openedIndex === 1}
        onClick={() => setOpenedIndex(1)}
        question={
          <Text color="primary.300" variant="label2medium">
            SCIENTIFIC RESULTS
          </Text>
        }
        questionStyle={
          isMobile
            ? {
                padding: '0 24px',
              }
            : {}
        }
        scrollIntoViewOnOpen
      />
      <Separator />
      <StyledCollapse
        answer={
          <BeforeAndAfter
            mention={content.beforeAndAfter.mention}
            slider={content.beforeAndAfter.slider}
            title={content.beforeAndAfter.title}
          />
        }
        chevronPadding={isMobile ? '0px 60px 0px 0px' : undefined}
        dataClick="toggle-collapse"
        dataFrom="accordion-3"
        isOpen={openedIndex === 2}
        onClick={() => setOpenedIndex(2)}
        question={
          <Text color="primary.300" variant="label2medium">
            BEFORE AND AFTER
          </Text>
        }
        questionStyle={
          isMobile
            ? {
                padding: '0 24px',
              }
            : {}
        }
        scrollIntoViewOnOpen
      />
      <Separator />
      <StyledCollapse
        answer={
          <AnswerContainer>
            <Text color="primary.300" paddingTop="2x" variant="body4regular">
              {content.fragranceFree}
            </Text>
          </AnswerContainer>
        }
        chevronPadding={isMobile ? '0px 60px 0px 0px' : undefined}
        dataClick="toggle-collapse"
        dataFrom="accordion-4"
        isOpen={openedIndex === 3}
        onClick={() => setOpenedIndex(3)}
        question={
          <Text color="primary.300" variant="label2medium">
            NATURALLY FRAGRANCE FREE
          </Text>
        }
        questionStyle={
          isMobile
            ? {
                padding: '0 24px',
              }
            : {}
        }
        scrollIntoViewOnOpen
      />
      <Separator />
    </Container>
  );
};
